import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Plus } from 'lucide-react';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';
import { TabelaPartes } from '@/components/apreciacao/tabela-partes';
import { ModalEditarParte } from '@/components/apreciacao/modal-editar-parte';

export function TemplateEdit() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useI18n();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Meta dados do template
    const [templateMeta, setTemplateMeta] = useState({
        nome: '',
        tipo: '',
        descricao: '',
        norma: 'NR-12'
    });

    // Estrutura de Partes e Riscos
    const [riscos, setRiscos] = useState<any[]>([]);
    const [modalParteAberto, setModalParteAberto] = useState(false);
    const [parteSelecionada, setParteSelecionada] = useState<{ parte: string; riscos: any[]; numero: number } | null>(null);

    useEffect(() => {
        if (id) {
            carregarTemplate(id);
        }
    }, [id]);

    const carregarTemplate = async (templateId: string) => {
        setLoading(true);
        try {
            const response = await apiService.buscarTemplate(templateId);
            if (response.success && response.data) {
                const t = response.data;

                setTemplateMeta({
                    nome: t.nome || '',
                    tipo: t.tipo || '',
                    descricao: t.descricao || '',
                    norma: t.norma || 'NR-12'
                });

                if (t.riscosPadrao && Array.isArray(t.riscosPadrao)) {
                    setRiscos(t.riscosPadrao.map((r: any) => ({
                        ...r,
                        id: r.id || crypto.randomUUID()
                    })));
                }
            } else {
                alert(t('templates.estado.erroCarregar', {
                    mensagem: response.message || t('templates.estado.erroDesconhecido'),
                }));
                navigate('/meus-templates');
            }
        } catch (err) {
            console.error(err);
            alert(t('templates.estado.erroCarregarInesperado'));
        } finally {
            setLoading(false);
        }
    };

    const handleChangeMeta = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setTemplateMeta(prev => ({ ...prev, [name]: value }));
    };

    const handleAdicionarParte = () => {
        const partesUnicas = new Set(riscos.map(r => r.parte || ''));
        const proximoNumero = partesUnicas.size + 1;
        let nomeParte = `Parte ${proximoNumero}`;
        let contador = 1;
        while (partesUnicas.has(nomeParte)) {
            contador++;
            nomeParte = `Parte ${proximoNumero} (${contador})`;
        }
        setParteSelecionada({ parte: nomeParte, riscos: [], numero: proximoNumero });
        setModalParteAberto(true);
    };

    const handleEditarParte = (parte: string, riscosParte: any[]) => {
        const partesUnicas = Array.from(new Set(riscos.map(r => r.parte || '')));
        const ind = partesUnicas.indexOf(parte);
        const numero = ind >= 0 ? ind + 1 : partesUnicas.length + 1;
        setParteSelecionada({ parte, riscos: riscosParte, numero });
        setModalParteAberto(true);
    };

    const handleDeletarParte = (parte: string) => {
        setRiscos(riscos.filter(r => r.parte !== parte));
    };

    const handleDuplicarParte = (parte: string, riscosParte: any[]) => {
        const partesExistentes = new Set(riscos.map(r => r.parte || ''));
        let novoNomeParte = `${parte} (Cópia)`;
        let contador = 1;

        while (partesExistentes.has(novoNomeParte)) {
            contador++;
            novoNomeParte = `${parte} (Cópia ${contador})`;
        }

        const maxNumero = riscos.length > 0 ? Math.max(...riscos.map(r => r.numero || 0)) : 0;
        let proximoNumero = maxNumero + 1;

        const riscosDuplicados = riscosParte.map((r: any) => ({
            ...r,
            id: crypto.randomUUID(),
            numero: proximoNumero++,
            parte: novoNomeParte,
        }));
        setRiscos([...riscos, ...riscosDuplicados]);
    };

    const handleSalvarParte = async (dadosParte: any) => {
        const novoNomeParte = dadosParte.parte || parteSelecionada?.parte;
        const parteAntiga = dadosParte.parteAntiga || parteSelecionada?.parte;

        let novosRiscos = riscos.filter(r => r.parte !== parteAntiga);

        const maxNumero = novosRiscos.length > 0 ? Math.max(...novosRiscos.map(r => r.numero || 0)) : 0;
        let currentNum = maxNumero + 1;

        const riscosProcessar = dadosParte.riscos?.length > 0 ? dadosParte.riscos : [
            { id: crypto.randomUUID(), tipoPerigo: 'Mecânico', categoria: '', probabilidadeAtual: 1, frequenciaAtual: 1, gravidadeAtual: 1, exposicaoAtual: 1, hrnAtual: 1, conformeAtual: false }
        ];

        const riscosAtualizados = riscosProcessar.map((r: any) => ({
            ...r,
            id: r.id || crypto.randomUUID(),
            numero: r.numero || currentNum++,
            parte: novoNomeParte,
            fotoAtualUrl1: dadosParte.fotoParte1,
            fotoAtualUrl2: dadosParte.fotoParte2,
            fotoAtualUrl3: dadosParte.fotoParte3,
            fotoAtualUrl4: dadosParte.fotoParte4,
            cicloVida: dadosParte.ciclosVida?.join(', '),
            eventoPerigoso: dadosParte.eventoPerigoso,
            conformeAtual: dadosParte.conforme,
            tipoPerigo: dadosParte.tiposPerigo?.join(', '),
            perigosEncontrados: dadosParte.perigosEncontrados?.join(', '),
            consequencias: dadosParte.potenciaisConsequencias?.join(', '),
            potenciaisConsequenciasEspecificas: dadosParte.potenciaisConsequenciasEspecificas,
            origemEspecifica: dadosParte.origemEspecifica,
            partesCorpoAtingidas: dadosParte.partesCorpoAtingidas,
            adequacoesTecnicas: dadosParte.descricaoCondicaoAtual?.join('\n'),
            medidasControle: dadosParte.medidasControle?.join('\n'),
            exigenciasNormativas: dadosParte.exigenciasNormativas?.join('\n'),
            adequacoesNormativas: dadosParte.adequacoesNormativas?.join('\n'),
            pronta: dadosParte.pronta,
            exibirHRN: dadosParte.exibirHRN !== undefined ? dadosParte.exibirHRN : r.exibirHRN,
            exibirSRPCS: dadosParte.exibirSRPCS !== undefined ? dadosParte.exibirSRPCS : r.exibirSRPCS,
        }));

        setRiscos([...novosRiscos, ...riscosAtualizados]);
        setModalParteAberto(false);
    };

    const handleFecharModal = () => {
        setModalParteAberto(false);
        setParteSelecionada(null);
    };

    const handleSalvar = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!templateMeta.nome || !templateMeta.tipo || !templateMeta.norma) {
            alert(t('templates.editar.erroCamposObrigatorios'));
            return;
        }

        setSaving(true);
        try {
            // A lista já está flat
            const riscosPadrao = riscos;

            const payload = {
                nome: templateMeta.nome,
                tipo: templateMeta.tipo,
                descricao: templateMeta.descricao,
                norma: templateMeta.norma,
                configuracao: {}, // Mantendo vazio por simplicidade, ou poderia carregar o que já tinha se preciso
                riscosPadrao,
            };

            if (!id) return;

            const response = await apiService.atualizarTemplate(id, payload);

            if (response.success && response.data) {
                alert(t('templates.editar.sucesso'));
                navigate('/meus-templates');
            } else {
                const errorMessage = response.errors?.length
                    ? response.errors.join('\n')
                    : response.message || t('templates.estado.erroAtualizar');
                alert(t('templates.estado.erroAtualizarDetalhe', { mensagem: errorMessage }));
            }
        } catch (error) {
            console.error('Erro ao salvar template:', error);
            alert(t('templates.estado.erroSalvar'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-schmersal-blue mx-auto"></div>
                    <p className="mt-4 text-sm text-gray-500">{t('templates.estado.carregandoTemplate')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-0">
                <div className="flex items-center">
                    <Link
                        to="/meus-templates"
                        className="mr-4 p-2.5 rounded-lg text-schmersal-gray-medium hover:text-schmersal-blue hover:bg-schmersal-gray-light transition-colors duration-200"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </Link>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-schmersal-blue dark:text-white">{t('templates.editar.titulo')}</h1>
                        <p className="mt-1 text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
                            {t('templates.editar.subtitulo')}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSalvar} className="space-y-6">
                {/* Dados base do Template */}
                <div className="bg-white dark:bg-schmersal-gray-darker p-6 rounded-lg shadow-sm border border-rg-line dark:border-schmersal-gray-dark">
                    <h2 className="text-xl font-bold text-schmersal-blue dark:text-white mb-4">{t('templates.form.secaoInformacoes')}</h2>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                            <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white">{t('templates.form.nome')}</label>
                            <input
                                type="text"
                                name="nome"
                                required
                                value={templateMeta.nome}
                                onChange={handleChangeMeta}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-schmersal-blue focus:border-schmersal-blue sm:text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white">{t('templates.form.tipoReferencia')}</label>
                            <input
                                type="text"
                                name="tipo"
                                required
                                disabled
                                value={templateMeta.tipo}
                                className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-schmersal-blue focus:border-schmersal-blue sm:text-sm text-gray-600"
                                title={t('templates.form.tipoReferenciaAjuda')}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white">{t('templates.form.norma')}</label>
                            <select
                                name="norma"
                                required
                                value={templateMeta.norma}
                                onChange={handleChangeMeta}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-schmersal-blue focus:border-schmersal-blue sm:text-sm"
                            >
                                <option value="NR-12">NR-12</option>
                                <option value="ISO 12100">ISO 12100</option>
                                <option value="ISO 23125">ISO 23125</option>
                            </select>
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white">{t('templates.form.descricao')}</label>
                            <textarea
                                name="descricao"
                                rows={2}
                                value={templateMeta.descricao}
                                onChange={handleChangeMeta}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-schmersal-blue focus:border-schmersal-blue sm:text-sm"
                            />
                        </div>
                    </div>
                </div>

                {/* Lista de Partes */}
                <div className="bg-white dark:bg-schmersal-gray-darker p-6 rounded-lg shadow-sm border border-rg-line dark:border-schmersal-gray-dark">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h2 className="text-xl font-bold text-schmersal-blue dark:text-white">{t('templates.partes.titulo')}</h2>
                            <p className="text-sm text-gray-500 mt-1">{t('templates.editar.partesSubtitulo')}</p>
                        </div>
                        <button
                            type="button"
                            onClick={handleAdicionarParte}
                            className="inline-flex items-center px-4 py-2 bg-blue-50 text-schmersal-blue border border-blue-200 rounded-md hover:bg-blue-100 font-medium text-sm transition-colors"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            {t('templates.partes.adicionar')}
                        </button>
                    </div>

                    <TabelaPartes
                        riscos={riscos}
                        onAdicionar={handleAdicionarParte}
                        onEditar={handleEditarParte}
                        onDeletar={handleDeletarParte}
                        onDuplicar={handleDuplicarParte}
                        modoEdicao={true}
                    />

                    {modalParteAberto && parteSelecionada && (
                        <ModalEditarParte
                            aberto={modalParteAberto}
                            parte={parteSelecionada.parte}
                            riscos={parteSelecionada.riscos}
                            numeroParte={parteSelecionada.numero}
                            onFechar={handleFecharModal}
                            onSalvar={handleSalvarParte}
                            descricaoMaquina={templateMeta.nome || ''}
                        />
                    )}
                </div>

                {/* Actions */}
                <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t-2 border-schmersal-gray-light p-4 shadow-xl flex justify-end gap-4 sm:px-6 lg:px-8">
                    <Link
                        to="/meus-templates"
                        className="w-full sm:w-auto text-center bg-white py-2.5 px-5 border border-rg-line rounded-lg shadow-sm text-sm font-semibold text-schmersal-gray-dark hover:bg-schmersal-gray-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-schmersal-blue transition-all duration-200"
                    >
                        {t('comum.acao.cancelar')}
                    </Link>
                    <button
                        type="submit"
                        disabled={saving}
                        className="w-full sm:w-auto inline-flex justify-center items-center px-5 py-2.5 border-2 border-transparent rounded-lg shadow-sm text-sm font-semibold text-white bg-schmersal-blue hover:bg-schmersal-blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-schmersal-blue disabled:opacity-50 transition-all duration-200"
                    >
                        <Save className="h-5 w-5 mr-2" />
                        {saving ? t('comum.acao.salvando') : t('templates.form.salvarAlteracoes')}
                    </button>
                </div>
            </form>
        </div>
    );
}
